import { isPlatformBrowser } from '@angular/common';
import {
    AfterViewInit,
    Directive,
    ElementRef,
    HostListener,
    Inject,
    Input,
    NgZone,
    OnDestroy,
    PLATFORM_ID,
    Renderer2,
} from '@angular/core';

import { EnvironmentService, IdUtils } from '@ecm/consumer';
import { EcmDispatcherModule, EnvUtils } from '@ecm/dispatcher';

@Directive({
    selector: '[ecmPushTracker]',
})
export class MaterialPushTrackerDirective implements AfterViewInit, OnDestroy {
    private static CLASS_NAME = 'MaterialPushTrackerDirective';

    /* eslint-disable @angular-eslint/no-input-rename */
    @Input('pdb-zid') public pdb_zid: string;
    @Input('pdb-sc') public pdb_sc: string;
    @Input('pdb-idc') public pdb_idc: string;
    @Input('pdb-ta') public pdb_ta: string;
    @Input('pdb-vi') public pdb_vi: string;
    /* eslint-enable @angular-eslint/no-input-rename */

    private readonly trackingId: string = 'UA-82378354-6';

    private tracked = false;

    private abortController = new AbortController();

    public constructor(
        @Inject(PLATFORM_ID) private platformId: any,
        private renderer: Renderer2,
        private element: ElementRef,
        private environmentService: EnvironmentService,
        private zone: NgZone,
    ) {}

    public ngAfterViewInit() {
        this.trackViewIfElementInViewport();
        this.zone.runOutsideAngular(() => {
            window.addEventListener(
                'scroll',
                () => {
                    this.trackViewIfElementInViewport();
                },
                {
                    passive: true,
                    signal: this.abortController.signal,
                },
            );
        });
    }

    public ngOnDestroy(): void {
        this.abortController.abort();
    }

    private trackViewIfElementInViewport(): void {
        if (!this.tracked && !EnvUtils.isEclair()) {
            // Get push position relatively to viewport
            const boundingClientRect: DOMRect = this.element.nativeElement.getBoundingClientRect();

            // Test if push is visible in viewport
            if (
                boundingClientRect.top > 0 &&
                boundingClientRect.top < window.innerHeight &&
                (boundingClientRect.bottom < window.innerHeight || boundingClientRect.height > window.innerHeight)
            ) {
                this.track('view');
            }
        }
    }

    @HostListener('click', ['$event'])
    private click(): void {
        if (!EnvUtils.isEclair()) {
            this.track('click');
        }
    }
    /**
     * Performs capping request through navigator.sendBeacon API if available, or tracking pixel
     * @param {string} eventType
     */
    private track(eventType: string): void {
        if (isPlatformBrowser(this.platformId)) {
            const env = this.environmentService.instant();
            if (env.API.TRACKER.PIXEL_URL) {
                const baseUrl: string = env.API.TRACKER.PIXEL_URL;
                const queryString: string = this.getTrackingQueryString(eventType);
                const url: string = baseUrl.concat(queryString);
                navigator && navigator.sendBeacon ? navigator.sendBeacon(url) : this.insertTrackingPixel(url);
                this.tracked = true;
            } else {
                EcmDispatcherModule.sendConsoleError(
                    MaterialPushTrackerDirective.CLASS_NAME,
                    "Couldn't perform tracking (env.TRACKER.PIXEL_URL key is false or undefined)",
                );
            }
        }
    }

    /**
     * Insert and remove a tracking pixel (1x1 px image) to perform capping request
     * @param {string} url
     */
    private insertTrackingPixel(url: string): void {
        EcmDispatcherModule.sendConsoleInfo(
            MaterialPushTrackerDirective.CLASS_NAME,
            'sendBeacon method is not available, will insert and remove tracking pixel instead',
        );
        const img: HTMLImageElement = new Image();
        img.width = 1;
        img.height = 1;
        img.onload = () => {
            this.renderer.removeChild(document.body, img);
        };
        img.src = url;
        this.renderer.appendChild(document.body, img);
    }

    /**
     * Generates query string for capping request
     * @param {string} eventType
     * @returns {string}
     */
    private getTrackingQueryString(eventType: string): string {
        const ts: number = Date.now();
        const pvuid: string = IdUtils.generateRandomId();
        // initialize with global variables
        let queryString: string =
            '?eventType=' +
            eventType +
            '&ts=' +
            ts.toString() +
            '&gaProps=' +
            this.trackingId +
            '&gaClientId=' +
            (globalThis.o_idzone?.['USER_EXTERNAL_ID_HASH'] || '') +
            '&pvuid=' +
            pvuid +
            '&url=' +
            encodeURIComponent(window.location.href);

        // add inputs variables
        if (this.pdb_zid) {
            queryString += '&zid=' + encodeURIComponent(this.pdb_zid);
        }
        if (this.pdb_sc) {
            queryString += '&sc=' + encodeURIComponent(this.pdb_sc);
        }
        if (this.pdb_idc) {
            queryString += '&idc=' + encodeURIComponent(this.pdb_idc);
        }
        if (this.pdb_ta) {
            queryString += '&ta=' + encodeURIComponent(this.pdb_ta);
        }
        if (this.pdb_vi) {
            queryString += '&vi=' + encodeURIComponent(this.pdb_vi);
        }
        return queryString;
    }
}
